import { useReducer } from 'react'
import { useNotifier } from '@/lib/notifier'
import { appState } from '@/store/appState'
import { useSendReassurance } from './useSendReassurance'
import { InitialView } from './InitialView'
import { HistoryView } from './HistoryView'
import { DirectionsView } from './DirectionsView'
import type { AlertRecord } from '@/types/firestore'

/* Port of ActionsBottomSheetWidget. NOT a real bottom sheet: the page pins
 * it to the bottom of the Stack (Align(0,1)); the 96x2 grabber is purely
 * decorative — no drag gesture exists. (DraggableScrollableSheetWidget is
 * dead code behind `if (false)` in the original.)
 *
 * View switching parity (THE subtle bit):
 *  - "see history" / "Close" write currentBottomSheetView WITHOUT update()
 *    and call safeSetState -> ONLY this component re-renders (forceRender);
 *    the page & map keep the stale view until someone publishes.
 *  - "Get directions" / "Go back" DO publish (update()).
 *
 * Shell: Padding(8,0,8,0) -> Container(bg 0xE6FFFFFF, top radius 20) ->
 * Column(min) with the grabber first. Final else -> empty container
 * (unreachable). */

export interface SheetProps {
  /** valueOrDefault(alarm.userName, '- ') — note the trailing space. */
  userName: string
  alerts: AlertRecord[] | null
  /** Owner uid (alert.ref.parent.parent.id), NOT the route :id. */
  userId: string | null
}

export function ActionsBottomSheet(props: SheetProps) {
  useNotifier(appState, (s) => s.currentBottomSheetView) // context.watch
  const [, forceRender] = useReducer((n: number) => n + 1, 0) // safeSetState
  const send = useSendReassurance(props.userId, props.alerts)

  const view = appState.currentBottomSheetView

  return (
    <div data-sheet style={{ padding: '0 8px' }}>
      <div
        style={{
          width: '100%',
          background: 'var(--sheet-bg)',
          borderRadius: '20px 20px 0 0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* Decorative grabber — no gesture attached. */}
        <div style={{ paddingTop: 10 }}>
          <div style={{ width: 96, height: 2, background: 'var(--handle)', borderRadius: 2 }} />
        </div>

        {view === 'initial' ? (
          <InitialView {...props} send={send} forceRender={forceRender} />
        ) : view === 'history' ? (
          <HistoryView {...props} forceRender={forceRender} />
        ) : view === 'directions' ? (
          <DirectionsView send={send} />
        ) : (
          <div /> // unreachable final else
        )}
      </div>
    </div>
  )
}
